<template>
  <Screen :back="false" title="personalize">
    <!-- Heading + subheading -->
    <div class="personalize-heading-area">
      <h2 class="personalize-heading" aria-label="Personalize">
        <span class="personalize-heading__shadow">Personalize</span>
        <span class="personalize-heading__text">Personalize</span>
      </h2>
      <p class="personalize-subheading">
        <span class="personalize-subheading__shadow">You chose: {{ foldLabel }}</span>
        <span class="personalize-subheading__text">You chose: {{ foldLabel }}</span>
      </p>
      <p class="personalize-hint">
        <span class="personalize-hint__shadow">To remove items — drag off the paper</span>
        <span class="personalize-hint__text">To remove items — drag off the paper</span>
      </p>
    </div>

    <!-- Main container: tabs + [actions | canvas] -->
    <div class="personalize-container">
      <!-- Work area: actions alongside canvas -->
      <div class="personalize-work-area">
        <!-- Action buttons — left column on desktop, below on mobile -->
        <div class="page-actions">
          <!-- Print button -->
          <form @submit.prevent="handlePrint" ref="desktopPrintForm" class="print-form" method="POST" action="/pdfs" target="pdf_window" enctype="multipart/form-data">
            <input type="hidden" name="data" ref="data" />
            <button type="submit" class="action-btn">
              <Printer :size="16" />
              <span class="action-btn__label">Print</span>
            </button>
          </form>
          <!-- Collapse / expand arrow for toggling palette section on desktop -->
          <button
            v-if="actionsExpanded && hasTextSelected"
            class="action-btn action-btn--round"
            @click="toggleActions"
            :aria-label="actionsExpanded ? 'Collapse toolbar' : 'Expand toolbar'"
          >
            <ChevronLeft v-if="actionsExpanded" :size="24" />
            <ChevronRight v-else :size="24" />
          </button>
          <!-- Attribute buttons (Color, Alignment, Font) — shown when text selected & expanded -->
          <template v-if="actionsExpanded && hasTextSelected">
            <button
              class="action-btn"
              :class="{ 'action-btn--active': activeDrawer === 'color' }"
              @click="toggleDrawer('color')"
            >
              <span class="action-btn__label">Color</span>
            </button>
            <button
              class="action-btn"
              :class="{ 'action-btn--active': activeDrawer === 'textAlign' }"
              @click="toggleDrawer('textAlign')"
            >
              <span class="action-btn__label">Alignment</span>
            </button>
            <button
              class="action-btn"
              :class="{ 'action-btn--active': activeDrawer === 'fontFamily' }"
              @click="toggleDrawer('fontFamily')"
            >
              <span class="action-btn__label">Font</span>
            </button>
          </template>
          <!-- Image UI: shown when activeDrawer === 'image' -->
          <template v-if="activeDrawer === 'image'">
            <button class="action-btn action-btn--round" @click="toggleDrawer('image')">
              <ChevronLeft :size="20" />
            </button>
            <ImageUpload class="action-btn action-btn--rect indent">
              <FolderIcon :size="16" class="mr-2" />
              <span class="action-btn__label">Browse</span>
            </ImageUpload>
            <CameraUpload ref="cameraUploadDesktop" class="action-btn action-btn--rect indent" @request-camera="showCameraPermission = true" @no-camera="showNoCameraFound = true">
              <CameraIcon :size="16" class="mr-2" />
              <span class="action-btn__label">Camera</span>
            </CameraUpload>
          </template>
          
          <!-- Add text / Add image — shown when NOT in attribute editing mode AND NOT adding image -->
          <template v-if="(!actionsExpanded || !hasTextSelected) && activeDrawer !== 'image'">
            <button class="action-btn" @click="addTextBox">
              <span class="action-btn__icon-placeholder"></span>
              <span class="action-btn__label">Add text box</span>
            </button>
            <button class="action-btn" @click="toggleDrawer('image')">
              <ImageIcon :size="16" class="mr-2" />
              <span class="action-btn__label">Add image</span>
            </button>
          </template>
        </div>
        <!-- Canvas and Palette grouped to align properly -->
        <div class="canvas-and-palette" :class="{ 'canvas-landscape': !!canvasLayout.wrapperHeight }" :style="{ width: canvasLayout.panelWidth }">
          <!-- Canvas with right-side page tabs -->
          <div class="canvas-with-tabs">
            <div class="canvas-wrapper" :style="canvasLayout.wrapperHeight ? { height: canvasLayout.wrapperHeight } : {}">
              <Canvas />
            </div>
            <Atlas class="atlas-sidebar" />
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile action bar — visible only on mobile -->
    <div class="mobile-action-bar">
      <!-- Mobile Text sub-actions: Color, Alignment, Font -->
      <template v-if="actionsExpanded && hasTextSelected && activeDrawer !== 'image'">
        <button class="action-btn action-btn--round" @click="toggleActions">
          <ChevronLeft :size="20" />
        </button>
        <button
          class="action-btn"
          :class="{ 'action-btn--active': activeDrawer === 'color' }"
          @click="toggleDrawer('color')"
        >
          <span class="action-btn__label">Color</span>
        </button>
        <button
          class="action-btn"
          :class="{ 'action-btn--active': activeDrawer === 'textAlign' }"
          @click="toggleDrawer('textAlign')"
        >
          <span class="action-btn__label">Alignment</span>
        </button>
        <button
          class="action-btn"
          :class="{ 'action-btn--active': activeDrawer === 'fontFamily' }"
          @click="toggleDrawer('fontFamily')"
        >
          <span class="action-btn__label">Font</span>
        </button>
      </template>

      <!-- Mobile Image UI -->
      <template v-else-if="activeDrawer === 'image'">
        <button class="action-btn action-btn--round" @click="toggleDrawer('image')">
          <ChevronLeft :size="20" />
        </button>
        <ImageUpload class="action-btn">
          <FolderIcon :size="16" class="mr-2" />
          <span class="action-btn__label">Browse</span>
        </ImageUpload>
        <CameraUpload ref="cameraUploadMobile" class="action-btn" @request-camera="showCameraPermission = true" @no-camera="showNoCameraFound = true">
          <CameraIcon :size="16" class="mr-2" />
          <span class="action-btn__label">Camera</span>
        </CameraUpload>
      </template>

      <!-- Mobile Default Actions -->
      <template v-else>
        <button class="action-btn" @click="addTextBox">
          <span class="action-btn__icon-placeholder"></span>
          <span class="action-btn__label">Add text box</span>
        </button>
        <button class="action-btn" @click="toggleDrawer('image')">
          <ImageIcon :size="16" class="mr-2" />
          <span class="action-btn__label">Add image</span>
        </button>
        <form @submit.prevent="handlePrint" ref="mobilePrintForm" class="print-form" method="POST" action="/pdfs" target="pdf_window" enctype="multipart/form-data">
          <input type="hidden" name="data" ref="dataMobile" />
          <button type="submit" class="action-btn">
            <Printer :size="16" />
            <span class="action-btn__label">Print</span>
          </button>
        </form>
      </template>
    </div>

    <!-- Palette (toolbar drawers) — below action bar on mobile, below work area on desktop -->
    <Palette ref="palette" class="personalize-palette" :class="{ 'personalize-palette--open': activeDrawer && activeDrawer !== 'image' }" />

    <!-- Top nav back button override -->
    <template v-slot:action>
      <button class="back-to-fold-btn" @click="handleBack">
        <ChevronLeft :size="16" />
        <span>Back to choose fold</span>
      </button>
    </template>

    <!-- Navigation confirm dialog -->
    <ConfirmDialog
      :visible="showBackConfirm"
      cancelLabel="Cancel"
      confirmLabel="Discard sketch and start over"
      @cancel="showBackConfirm = false"
      @confirm="confirmBack"
    >
      <template v-slot:title>Scrapping this design?</template>
      We don't store your sketches on our drafting table. If you leave now, this card will be permanently discarded. Finish and print to keep your work!
    </ConfirmDialog>

    <!-- Camera permission dialog -->
    <ConfirmDialog
      :visible="showCameraPermission"
      cancelLabel="Cancel"
      confirmLabel="Go ahead, I want to take a photo"
      @cancel="showCameraPermission = false"
      @confirm="onCameraPermissionConfirmed"
    >
      <template v-slot:icon><img :src="helpIcon" class="dialog-help-icon" alt="?" /></template>
      <template v-slot:title>Do you want to allow {{ hostname }} to use your camera?</template>
      You tapped "Camera" to activate your camera to add a photo you are about to take to the page. You have to give us permission.
    </ConfirmDialog>

    <!-- Camera capture modal -->
    <CameraCapture
      :visible="showCameraCapture"
      @captured="onPhotoCaptured"
      @cancel="showCameraCapture = false"
    />

    <!-- No camera dialog -->
    <ConfirmDialog
      :visible="showNoCameraFound"
      confirmLabel="Close"
      @confirm="showNoCameraFound = false"
      @cancel="showNoCameraFound = false"
    >
      <template v-slot:title>No camera found</template>
      Sorry. There is no camera able to be detected on your device. Try browsing for an image instead.
    </ConfirmDialog>

    <!-- Rotate device blocking overlay — Bifold Easel on narrow portrait only.
         Auto-dismisses when device is rotated; GOT IT is informational only. -->
    <transition name="rotate-overlay-fade">
      <div v-if="isNarrowLandscapeFold" class="rotate-overlay">
        <div class="rotate-box">
          <div class="rotate-icon-wrap">
            <img :src="refreshIcon" class="rotate-icon" alt="Rotate device" />
          </div>
          <h2 class="rotate-title">Landscape View Required</h2>
          <p class="rotate-body">The Bifold Easel requires a wider drafting surface for a true-to-scale preview. Please rotate your device to landscape to continue your design.</p>
          <button class="rotate-btn">Got It</button>
        </div>
      </div>
    </transition>

    <!-- Print how-to-fold dialog -->
    <transition name="print-dialog-fade">
      <div v-if="showPrintDialog" class="print-dialog-overlay">
        <div class="print-dialog-box">
          <div class="print-dialog-header">
            <div class="print-dialog-icon">
              <InformationOutline :size="32" />
            </div>
            <h2 class="print-dialog-title">Your PDF is Ready!</h2>
          </div>
          <div class="print-dialog-content">
            <div v-if="foldVideo" class="print-dialog-left">
              <video
                class="print-dialog-video"
                :src="foldVideo.src"
                :poster="foldVideo.poster"
                controls
                autoplay
                playsinline
              ></video>
            </div>
            <div class="print-dialog-right">
              <div v-if="isTwoPagePrint" class="print-special-instructions">
                <strong>Double-Sided Printing Required!</strong>
                You will have a TWO page PDF. Make sure ONLY one piece of paper is in your printer! Print page 1, THEN feed this page back into your printer, printed side up, with the top of the card away from you.
              </div>
              <p class="print-dialog-body">Here is how to fold your {{ foldLabel }}.</p>
              <div class="print-dialog-actions">
                <button class="print-dialog-secondary-btn" @click="showPrintDialog = false">Close</button>
                <button
                  class="print-dialog-primary-btn"
                  @click="focusPdfTab"
                  :disabled="isGeneratingPdf"
                >{{ isGeneratingPdf ? 'Generating PDF...' : 'Go to PDF Tab to Print' }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <AppFooter currentScreen="PersonalizeScreen" />
  </Screen>
</template>

<script>
import Screen from './Screen.vue';
import AppFooter from './AppFooter.vue';
import CameraCapture from './CameraCapture.vue';
import helpIcon from '../assets/icons/Icon-Help.svg';
import refreshIcon from '../assets/icons/Icon-Refresh.svg';
import Atlas from './Atlas.vue';
import Canvas from './Canvas.vue';
import Palette from './Palette.vue';
import ConfirmDialog from './ConfirmDialog.vue';
import ImageUpload from './Image.vue';
import CameraUpload from './Camera.vue';
import FolderIcon from 'vue-material-design-icons/FolderOutline.vue';
import CameraIcon from 'vue-material-design-icons/CameraOutline.vue';
import Printer from 'vue-material-design-icons/Printer.vue';
import ImageIcon from 'vue-material-design-icons/Image.vue';
import ChevronLeft from 'vue-material-design-icons/ChevronLeft.vue';
import ChevronRight from 'vue-material-design-icons/ChevronRight.vue';
import InformationOutline from 'vue-material-design-icons/InformationOutline.vue';

export default {
  components: {
    Screen,
    AppFooter,
    CameraCapture,
    Atlas,
    Canvas,
    Palette,
    ConfirmDialog,
    ImageUpload,
    CameraUpload,
    FolderIcon,
    CameraIcon,
    Printer,
    ImageIcon,
    ChevronLeft,
    ChevronRight,
    InformationOutline
  },
  data() {
    return {
      actionsExpanded: false,
      activeDrawer: null,
      showBackConfirm: false,
      showCameraPermission: false,
      showNoCameraFound: false,
      showCameraCapture: false,
      showPrintDialog: false,
      viewportWidth: window.innerWidth,
      helpIcon,
      refreshIcon,
      hostname: window.location.hostname,
      pdfUrl: null,
      isGeneratingPdf: false
    };
  },
  computed: {
    canvasLayout() {
      const fold = this.$store.state.fold;
      if (!fold) return { panelWidth: '500px', wrapperHeight: null };
      const pageRatio = fold.width / fold.height;
      if (pageRatio >= 1) {
        // Landscape fold: fix panel at 500px, shrink height to match ratio
        return {
          panelWidth: '500px',
          wrapperHeight: `${Math.round(500 / pageRatio)}px`
        };
      }
      // Portrait fold: height fills work area (~395px), width = height × ratio
      const availHeight = this.$store.state.canvasSize.height || 395;
      const panelWidth = Math.round(Math.min(500, availHeight * pageRatio));
      return {
        panelWidth: `${panelWidth}px`,
        wrapperHeight: null
      };
    },
    foldLabel() {
      const fold = this.$store.state.fold;
      return fold ? fold.label : '';
    },
    hasTextSelected() {
      return !!this.$store.state.itemId;
    },
    hasUserMadeChanges() {
      // Check if user has added any items to any page
      const pages = this.$store.state.pages;
      if (!pages) return false;
      // Check current canvas items
      if (this.$store.state.items && this.$store.state.items.length > 0) return true;
      // Check saved page items
      for (const page of pages) {
        if (page.items && page.items.length > 0) return true;
      }
      return false;
    },
    isNarrowLandscapeFold() {
      return !!this.canvasLayout.wrapperHeight && this.viewportWidth < 500;
    },
    foldVideo() {
      const fold = this.$store.state.fold;
      if (!fold) return null;
      const map = {
        'trifold':        { src: require('../assets/videos/trifold/trifold.mp4'),               poster: require('../assets/videos/trifold/trifold.jpg') },
        'bifold-vertical':{ src: require('../assets/videos/bifold-vertical/bifold-vertical.mp4'), poster: require('../assets/videos/bifold-vertical/bifold-vertical.jpg') },
        'quarter-fold':   { src: require('../assets/videos/quarterfold/quarterfold.mp4'),        poster: require('../assets/videos/quarterfold/quarterfold.jpg') },
        'bifold-easel':   { src: require('../assets/videos/bifold-easel/bifold-easel.mp4'),      poster: require('../assets/videos/bifold-easel/bifold-easel.jpg') }
      };
      return map[fold.name] || null;
    },
    isTwoPagePrint() {
      const fold = this.$store.state.fold;
      return fold && (fold.name === 'bifold-vertical' || fold.name === 'bifold-easel');
    }
  },
  mounted() {
    this._onResize = () => { this.viewportWidth = window.innerWidth; };
    window.addEventListener('resize', this._onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize);
  },
  watch: {
    hasTextSelected(newVal) {
      if (newVal) {
        this.actionsExpanded = true;
        // If coming from image-add mode, clear it so text toolbar shows correctly
        if (this.activeDrawer === 'image') {
          this.activeDrawer = null;
        }
      } else {
        this.actionsExpanded = false;
        this.activeDrawer = null;
      }
    }
  },
  methods: {
    async handlePrint() {
      if (this.isGeneratingPdf) return;
      this.$store.commit('commitPage');
      const payload = JSON.stringify({
        pages: this.$store.state.pages,
        fold: this.$store.state.fold
      });
      
      this.showPrintDialog = true;
      this.isGeneratingPdf = true;
      this.pdfUrl = null;

      const formData = new FormData();
      formData.append('data', payload);
      
      try {
        const response = await fetch('/pdfs', {
          method: 'POST',
          body: formData
        });
        if (response.ok) {
          this.pdfUrl = response.url;
        } else {
          console.error('Failed to generate PDF. Status:', response.status);
        }
      } catch (err) {
        console.error('Failed to execute PDF generation fetch', err);
      } finally {
        this.isGeneratingPdf = false;
      }
    },
    focusPdfTab() {
      if (this.pdfUrl) {
        window.open(this.pdfUrl, '_blank');
      }
    },
    handleBack() {
      if (this.hasUserMadeChanges) {
        this.showBackConfirm = true;
      } else {
        this.goBack();
      }
    },
    confirmBack() {
      this.showBackConfirm = false;
      this.goBack();
    },
    goBack() {
      this.$store.commit('setScreen', 'ChooseFoldScreen');
    },
    addTextBox() {
      // Trigger the existing text button logic in the store
      this.$store.commit('setButton', { id: 'text' });
      this.actionsExpanded = true;
    },
    toggleActions() {
      this.actionsExpanded = false;
      this.activeDrawer = null;
      this.$store.commit('setButton', null);
      this.$store.commit('setItemId', null);
    },
    onCameraPermissionConfirmed() {
      this.showCameraPermission = false;
      this.showCameraCapture = true;
    },
    onPhotoCaptured(dataUrl, width, height) {
      this.showCameraCapture = false;
      this.$store.commit('addItem', {
        type: 'ImageItem',
        id: require('cuid')(),
        content: {
          width: 144,
          height: height * (144 / width),
          left: this.$store.state.fold.width / 2 - 72,
          top: this.$store.state.fold.height / 2 - (height * (144 / width)) / 2,
          src: dataUrl
        }
      });
    },
    toggleDrawer(drawerId) {
      if (this.activeDrawer === drawerId) {
        this.activeDrawer = null;
        this.$store.commit('setButton', null);
      } else {
        this.activeDrawer = drawerId;
        // Find the matching button in the Palette's buttons data
        const palette = this.$refs.palette;
        if (palette && palette.buttons) {
          const btn = palette.buttons.find(b => b.id === drawerId);
          if (btn) {
            this.$store.commit('setButton', btn);
          }
        }
      }
    }
  }
}
</script>

<style scoped>
/* ── Camera dialog help icon ── */
.dialog-help-icon {
  width: 32px;
  height: 32px;
}

/* ── Rotate device blocking overlay ── */
.rotate-overlay {
  position: fixed;
  inset: 0;
  z-index: 300;
  background-color: rgba(0, 8, 7, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
}

.rotate-box {
  background-color: var(--surface-dark);
  border: 2px solid var(--text-bright);
  border-radius: 4px;
  padding: 36px 28px 28px;
  max-width: 500px;
  width: 88%;
  max-height: 90vh;
  overflow-y: auto;
  text-align: center;
  filter: url(#button-stroke);
  box-shadow: 0 8px 32px rgba(0, 8, 7, 0.6);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.rotate-icon-wrap {
  display: flex;
  justify-content: center;
}

/* Icon animates from portrait to landscape and back — communicates the gesture */
.rotate-icon {
  width: 40px;
  height: 40px;
  animation: rotate-device 2.4s ease-in-out infinite;
  transform-origin: center;
}

@keyframes rotate-device {
  0%   { transform: rotate(0deg); }
  35%  { transform: rotate(-90deg); }
  65%  { transform: rotate(-90deg); }
  100% { transform: rotate(0deg); }
}

.rotate-title {
  font-family: Cabin-Sketch, cursive;
  font-weight: bold;
  font-size: 22px;
  line-height: 1.2;
  color: var(--text-lightest);
  text-transform: uppercase;
  margin: 0;
}

.rotate-body {
  font-family: var(--font-family-balsamiq);
  font-size: 14px;
  line-height: 1.5;
  color: var(--text-lightest);
  margin: 0;
}

.rotate-btn {
  font-family: var(--font-family-balsamiq);
  font-size: 16px;
  line-height: 24px;
  padding: 7px 28px;
  border-radius: 1px;
  border: 2px solid var(--text-bright);
  background-color: var(--surface-darkest);
  color: var(--text-bright);
  filter: url(#button-stroke);
  cursor: default;
  white-space: nowrap;
}

/* Fade the overlay in/out */
.rotate-overlay-fade-enter-active,
.rotate-overlay-fade-leave-active {
  transition: opacity 0.3s ease;
}
.rotate-overlay-fade-enter,
.rotate-overlay-fade-leave-to {
  opacity: 0;
}

/* ── Hide the Screen component's default nav title ── */
:deep(.title) {
  display: none;
}

/* ── Heading area ── */
.personalize-heading-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 24px 0;
  flex-shrink: 0;
}

.personalize-heading {
  position: relative;
  font-family: Cabin-Sketch, cursive;
  font-weight: bold;
  font-size: 80px;
  line-height: 1.1;
  text-transform: uppercase;
  letter-spacing: 0.01em;
  text-align: center;
  margin: 0;
  border: none;
  min-height: 60px;
}

.personalize-heading__shadow {
  position: absolute;
  left: 6px;
  top: 6px;
  width: 100%;
  color: var(--surface-darkest);
  -webkit-text-stroke: 0;
}

.personalize-heading__text {
  position: relative;
  display: block;
  width: 100%;
  color: var(--green-lightest);
  -webkit-text-stroke: 1px var(--surface-darkest);
  paint-order: stroke fill;
}

.personalize-subheading {
  position: relative;
  font-family: var(--font-family-balsamiq);
  font-size: 24px;
  line-height: 1.3;
  text-align: center;
  margin: 0;
}

.personalize-subheading__shadow {
  position: absolute;
  left: 3px;
  top: 3px;
  width: 100%;
  color: var(--surface-darkest);
}

.personalize-subheading__text {
  position: relative;
  color: var(--green-lightest);
}

/* ── Hint line (delete instruction) ── */
.personalize-hint {
  position: relative;
  font-family: var(--font-family-balsamiq);
  font-size: 16px;
  line-height: 1.5;
  text-align: center;
  margin: 0;
}

.personalize-hint__shadow {
  position: absolute;
  left: 2px;
  top: 2px;
  width: 100%;
  color: var(--surface-darkest);
}

.personalize-hint__text {
  position: relative;
  color: var(--green-lightest);
}

/* ── Main container ── */
.personalize-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  flex-grow: 1;
  padding: 24px;
  max-width: 1464px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
}

/* ── Work area: actions + canvas side by side (desktop) ── */
.personalize-work-area {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 16px;
  width: 100%;
  justify-content: center;
  height: 431px;
}

/* ── Canvas + Palette grouping ── */
.canvas-and-palette {
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  gap: 0;
  height: 100%;
}

/* ── Canvas + right-side tabs row ── */
.canvas-with-tabs {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  flex: 1 1 auto;
  min-height: 0;
}

/* ── Canvas wrapper — flex-grow for portrait folds; height fixed for landscape via :style ── */
.canvas-wrapper {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  display: flex;
  overflow: hidden;
}

/* ── Atlas sidebar — right-side page tabs, flush against canvas ── */
.atlas-sidebar {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* ── Page action buttons (left column on desktop) ── */
.page-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 24px;
  flex-shrink: 0;
}

/* ── Action button — dark bg, aqua border, Figma style ── */
.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-color: var(--surface-darkest);
  border: 2px solid var(--text-bright);
  color: var(--text-bright);
  font-family: var(--font-family-balsamiq);
  font-size: 16px;
  line-height: 24px;
  padding: 7px 16px 7px 12px;
  border-radius: 1px;
  cursor: pointer;
  filter: url(#button-stroke);
  white-space: nowrap;
}

.action-btn:hover {
  background-color: var(--surface-darker);
}

.action-btn--active {
  background-color: var(--green-lightest);
  color: var(--text-dark);
  border-color: var(--text-dark);
}

.action-btn--active:hover {
  background-color: var(--surface-dark);
  color: var(--text-bright);
  border-color: var(--text-dark);
}

.action-btn--round {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--surface-darkest);
  border: 2px solid var(--text-bright);
}

.action-btn__icon-placeholder {
  width: 16px;
  height: 16px;
  border: 2px dashed var(--text-bright);
  flex-shrink: 0;
}

.action-btn--active .action-btn__icon-placeholder {
  border-color: var(--text-dark);
}

/* Label text inherits button styling from .action-btn */

/* ── Print form ── */
.print-form {
  display: flex;
}

/* ── Palette (toolbar drawers) ── */
.personalize-palette {
  max-width: 600px;
  margin: 16px auto 0;
  width: 100%;
}

/* Hide the Palette's own button bar — we drive actions from PersonalizeScreen */
.personalize-palette :deep(.buttons-box) {
  display: none;
}

/* ── Mobile action bar — hidden on desktop ── */
.mobile-action-bar {
  display: none;
}

/* ── Back to fold button in nav ── */
.back-to-fold-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-color: var(--surface-darkest);
  border: 2px solid var(--text-bright);
  color: var(--text-bright);
  font-family: var(--font-family-balsamiq);
  font-size: 16px;
  line-height: 24px;
  padding: 7px 16px 7px 12px;
  border-radius: 1px;
  cursor: pointer;
  filter: url(#button-stroke);
  white-space: nowrap;
}

.back-to-fold-btn:hover {
  background-color: var(--surface-darker);
}

/* ── Responsive: ≤960px ── */
@media screen and (max-width: 960px) {
  .personalize-heading {
    font-size: 56px;
    min-height: 48px;
  }
  .personalize-heading__shadow {
    left: 4px;
    top: 4px;
  }
  .personalize-subheading {
    font-size: 20px;
  }
}

/* ── Responsive: ≤560px — mobile layout ── */
@media screen and (max-width: 560px) {
  .personalize-heading {
    font-size: 32px;
    min-height: auto;
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: var(--surface-dark);
    padding: 4px 0;
  }
  .personalize-heading__shadow {
    left: 3px;
    top: 3px;
  }
  .personalize-subheading {
    font-size: 16px;
  }
  .personalize-heading-area {
    padding: 8px 16px 0;
  }
  .personalize-container {
    padding: 8px;
    flex-grow: 0;
  }
  .personalize-work-area {
    flex-direction: column;
    align-items: center;
    gap: 0;
    min-height: auto;
  }
  /* Landscape fold (e.g. Bifold Easel): shrink to actual canvas height on mobile.
     Portrait folds never receive .canvas-landscape so they are unaffected. */
  .canvas-landscape,
  .canvas-landscape .canvas-with-tabs {
    height: auto;
  }
  /* Hide desktop page-actions on mobile */
  .page-actions {
    display: none;
  }
  /* Show mobile action bar */
  .mobile-action-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    justify-content: center;
    padding: 16px 8px;
  }

  .personalize-palette {
    display: none;
  }
  .personalize-palette--open {
    display: block;
  }
  /* Back button: text may be long, use smaller font */
  .back-to-fold-btn {
    font-size: 14px;
    padding: 5px 10px 5px 8px;
  }
  .back-to-fold-btn span:last-child {
    display: none;
  }
}

/* ── Print how-to-fold dialog ── */
.print-dialog-overlay {
  position: fixed;
  inset: 0;
  z-index: 300;
  background-color: rgba(0, 8, 7, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
}

.print-dialog-box {
  background-color: var(--surface-dark);
  border: 2px solid var(--text-bright);
  border-radius: 4px;
  padding: 32px 32px 24px;
  max-width: 800px;
  width: 95%;
  max-height: 90vh;
  overflow-y: auto;
  filter: url(#button-stroke);
  box-shadow: 0 8px 32px rgba(0, 8, 7, 0.6);
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.print-dialog-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.print-dialog-icon {
  color: var(--text-bright);
  display: flex;
}

.print-dialog-title {
  font-family: Cabin-Sketch, cursive;
  font-weight: bold;
  font-size: 28px;
  line-height: 1.2;
  color: var(--text-lightest);
  text-transform: uppercase;
  margin: 0;
}

.print-dialog-content {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 32px;
}

.print-dialog-left {
  flex: 0 0 45%;
  min-width: 0;
}

.print-dialog-right {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.print-special-instructions {
  background-color: var(--surface-darker);
  border: 2px dashed var(--text-bright);
  padding: 16px;
  border-radius: 4px;
  font-family: var(--font-family-balsamiq);
  font-size: 14px;
  line-height: 1.5;
  color: var(--text-lightest);
  text-align: left;
  margin: 0 0 8px;
}

.print-special-instructions strong {
  display: block;
  font-family: Cabin-Sketch, cursive;
  font-size: 20px;
  color: var(--green-lightest);
  text-transform: uppercase;
  margin-bottom: 8px;
  text-align: center;
  letter-spacing: 0.5px;
}

.print-dialog-body {
  font-family: var(--font-family-balsamiq);
  font-size: 14px;
  line-height: 1.5;
  color: var(--text-lightest);
  margin: 0;
  text-align: left;
}

.print-dialog-video {
  width: 100%;
  max-height: 60vh;
  object-fit: contain;
  display: block;
  border-radius: 2px;
  background-color: var(--surface-darkest);
}

.print-dialog-actions {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  margin-top: auto;
}

/* Make stack vertically on small screens */
@media screen and (max-width: 768px) {
  .print-dialog-content {
    flex-direction: column;
    align-items: stretch;
    gap: 24px;
  }
  .print-dialog-left {
    flex: auto;
  }
  .print-dialog-video {
    max-height: 40vh;
  }
  .print-dialog-actions {
    justify-content: space-between;
  }
}

.print-dialog-secondary-btn {
  font-family: var(--font-family-balsamiq);
  font-size: 14px;
  line-height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--text-bright);
  text-decoration: underline;
  text-decoration-skip-ink: none;
  cursor: pointer;
}

.print-dialog-secondary-btn:hover {
  color: var(--text-lightest);
}

.print-dialog-primary-btn {
  font-family: var(--font-family-balsamiq);
  font-size: 14px;
  line-height: 24px;
  padding: 7px 20px;
  border-radius: 1px;
  border: 2px solid var(--text-bright);
  background-color: var(--surface-darkest);
  color: var(--text-bright);
  filter: url(#button-stroke);
  cursor: pointer;
  white-space: nowrap;
}

.print-dialog-primary-btn:hover {
  background-color: var(--surface-dark);
  color: var(--text-lightest);
}

.print-dialog-fade-enter-active,
.print-dialog-fade-leave-active {
  transition: opacity 0.25s ease;
}
.print-dialog-fade-enter,
.print-dialog-fade-leave-to {
  opacity: 0;
}
</style>